import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import type { ChoiceOption } from '@/components/ui/control'
import { useSession } from '@/features/auth/api/useSession'
import { useCase } from '@/features/cases/api/useCase'
import { refusalMessage } from '@/features/cases/lib/refusal'
import {
  toAllocationPayload,
  useAllocatePayment,
  useConfirmPayment,
} from '@/features/finance/api/useFinance'
import { PaymentAllocationSheet } from '@/features/finance/components/PaymentAllocationSheet'
import { RejectPaymentSheet } from '@/features/finance/components/RejectPaymentSheet'
import { PaymentStatusTag } from '@/features/finance/components/PaymentStatusTag'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber, formatTimestamp } from '@/lib/format'
import type { Payment } from '@/types/domain'

/* What one payment has been applied to, the act of applying it, and — since
 * 0059 — Finance's second pair of eyes on the record.
 *
 * The rows come from the PAYMENT the caller passes, which is the row the
 * server most recently confirmed — never from local state and never from the
 * mutation's own variables. That is the whole of UX-DR-24 here: allocating
 * invalidates the payment queries, the list refetches, and the new row appears
 * because the server said it exists. While the request is in flight the list
 * below is exactly what it was, and a refusal leaves it untouched with the
 * server's message on the sheet.
 *
 * A payment with nothing applied to it is not an error state and is not styled
 * as one: unallocated cash is legal (AD-11). It says what it is and offers the
 * act that would change it.
 *
 * ── The targets depend on the DIRECTION, and that was the defect ────────────
 *
 * This block used to offer the case's receivable tranches whatever the payment
 * was, so allocating an OUTBOUND payment offered the one thing it cannot
 * settle: the server refuses the mismatch outright
 * (`ErrAllocationDirectionMismatch`), and the empty state advised agreeing a
 * rate, which has nothing to do with paying a haulier. So the kind now follows
 * the direction — `out` settles this case's ACTUAL cost lines, `in` settles its
 * tranches — and the outbound targets are resolved HERE, out of the case the
 * payment names, because the sheet is a picker over choices rather than a
 * second reader of somebody else's rows.
 *
 * ── Confirm and reject, and what neither of them does to the money ──────────
 *
 * A payment recorded by Operations is born `pending` and counts in every figure
 * from that instant (spec L4): «Оплачено», the balances, the aging. Confirming
 * changes the RECORD's status and moves no sum, and rejecting DELETES the row
 * with a mandatory reason rather than marking it rejected — a rejected row left
 * on the ledger is a figure every sum then has to remember to exclude (L8).
 *
 * Both controls are hidden without `finance.payment.confirm`. That is an
 * AFFORDANCE and not the gate: the server re-checks the capability on the
 * request and answers 403 to a client that decided otherwise (AD-21).
 */

/* One row of the settlement list, and the legend above it. Stacked in a narrow
   container and four aligned columns once there is room — see the `@container`
   note in the render. Written once so the legend cannot drift from the rows it
   heads. */
const ROW =
  'border-line-hair grid grid-cols-1 gap-x-4 gap-y-0.5 border-b py-1.5 last:border-b-0 ' +
  '@2xl:grid-cols-[minmax(0,1fr)_8rem_13rem_8rem] @2xl:items-baseline'

export function PaymentAllocations({ payment }: { payment: Payment }) {
  const { t } = useTranslation()
  const { data: user } = useSession()
  const allocate = useAllocatePayment()
  const confirm = useConfirmPayment()
  const [open, setOpen] = useState(false)
  const [rejecting, setRejecting] = useState(false)

  const currency = payment.amount.currency
  const unallocated = Number(payment.unallocated.amount)
  const outbound = payment.direction === 'out'

  /* The case behind an OUTBOUND payment, for its recorded costs. Read through
     the case's own hook rather than a second endpoint of this feature's own:
     the rows are already keyed at `cases.detail(caseId)`, which every payment
     write in this file invalidates, so a cost line settled here disappears from
     the picker without anything extra being wired.

     Not asked at all for an inbound payment — the id is empty, the hook is
     disabled, and the sheet reads the case's tranches for itself. */
  const record = useCase(outbound ? payment.caseId : '')

  const expenseOptions = useMemo<ChoiceOption[]>(() => {
    const detail = record.data
    if (!detail) return []

    /* The plate, so two «Ставка фуры» lines on one case can be told apart. It
       is the only thing on a cost line that names the truck, and the case
       detail is already carrying it. */
    const plateOf = new Map(
      detail.legs.flatMap((leg) =>
        (leg.vehicles ?? []).map((vehicle) => [vehicle.id, vehicle.plate] as const),
      ),
    )

    /* ACTUAL lines only. A PLAN is refused by the server outright
       (`ErrExpenseLineIsNotADebt`) — nobody has invoiced it, and cash allocated
       against a forecast would leave the real cost reading as unsettled beside
       it. `actualId` is the id an allocation targets, and its absence is the
       whole of the distinction. */
    return (detail.money?.expenses ?? [])
      .filter((line) => line.actualId !== undefined)
      .map((line) => {
        const figure = line.actual ?? line.planned
        return {
          value: line.actualId ?? '',
          label: line.description,
          hint: [
            line.vehicleId ? plateOf.get(line.vehicleId) : undefined,
            formatMoney(figure.amount, figure.currency),
          ]
            .filter(Boolean)
            .join(' · '),
        }
      })
  }, [record.data])

  const pending = payment.status === 'pending'
  const mayConfirm = can(user, CAP.financePaymentConfirm)

  /* Refused with a SENTENCE rather than a bare disabled button: each of these
     is a reason the reader can act on rather than a transient state whose cause
     is already on screen. The costs still being read is the one that is
     genuinely transient, and it is stated anyway — the alternative is a sheet
     that opens saying this case has no costs while its read is still in flight,
     which is a lie the reader would act on. */
  const allocateBlocked =
    unallocated <= 0
      ? t('finance.allocations.fullyAllocated')
      : outbound && record.isPending
        ? t('finance.allocations.expenseTargetsLoading')
        : outbound && record.isError
          ? t('finance.allocations.expenseTargetsFailed')
          : undefined


  return (
    /* A CONTAINER query, not a viewport one, and it is the only way this block
       can be right in both of its homes: it is a full-width band under a
       payment row on the case's money card, and a ~450px column inside the
       Finance page's sheet. A `sm:` here would give the sheet four columns it
       cannot hold, because the viewport is wide either way. */
    <div className="@container flex flex-col gap-2.5 py-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className="asa-eyebrow">{t('finance.allocations.heading')}</span>
          <PaymentStatusTag status={payment.status} />
        </span>

        <span className="flex flex-wrap items-center gap-2">
          {/* Finance's two acts, offered only on a record that is still waiting
              and only to a reader who holds the capability. A confirmed payment
              shows neither: confirming twice is a no-op the server absorbs, and
              deleting a confirmed one is refused (`ErrPaymentNotPending`). */}
          {pending && mayConfirm ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate(payment.id)}
              >
                {t('finance.payments.confirm')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRejecting(true)}
              >
                {t('finance.payments.reject')}
              </Button>
            </>
          ) : null}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setOpen(true)}
            blockedReason={allocateBlocked}
          >
            {t('finance.allocations.allocate')}
          </Button>
        </span>
      </div>

      {/* A refused confirm has nowhere else to go — there is no sheet holding a
          draft — so it is said here, in the reader's own language. */}
      {confirm.error ? (
        <p className="text-negative text-[12.5px] leading-snug">
          {refusalMessage(confirm.error, t)}
        </p>
      ) : null}

      {payment.allocations.length === 0 ? (
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('finance.allocations.none')}
        </p>
      ) : (
        <>
        {/* The column heading, and it earns its line on «по 1 · 2 500.00 USD»
            alone: the settlement booking read as a bare fragment beside two
            other numbers is the one cell on this row nobody could name. Its own
            element rather than a first `<li>`, so the list holds settlements
            and nothing else; hidden where the columns are stacked and each row
            is read down instead of across. */}
        <div className={`${ROW} text-text-muted hidden text-[10px] font-medium tracking-wide uppercase @2xl:grid`}>
          <span>{t('finance.allocations.columns.target')}</span>
          <span className="text-right">{t('finance.allocations.columns.amount')}</span>
          <span className="text-right">{t('finance.allocations.columns.booked')}</span>
          <span className="text-right">{t('finance.allocations.columns.at')}</span>
        </div>

        <ul className="flex flex-col">
          {payment.allocations.map((allocation) => {
            // Signed, and the sign is the information: a settlement worth less
            // in USD than the figure the target was booked at is a loss on the
            // rate, and it is recorded rather than smoothed away (AD-9).
            const difference = Number(allocation.fxSettlementDifference)
            return (
              <li key={allocation.id} className={ROW}>
                <span className="text-text-body text-[13px] [overflow-wrap:anywhere]">
                  {allocation.targetLabel}
                </span>
                <span className="asa-num text-text-heading text-[13px] @2xl:text-right">
                  {formatMoney(allocation.amount.amount, allocation.amount.currency)}
                </span>
                {/* The settlement booking, not the target's. The two differ by
                    exactly the figure beside them, and the target's own booked
                    USD is never rewritten to close the gap. The difference
                    travels in THIS cell rather than in one of its own: a column
                    that is empty on every allocation but the odd one is a
                    column of nothing, and the two facts are one sentence about
                    the rate. */}
                <span className="asa-num text-text-muted text-[12px] @2xl:text-right">
                  {t('finance.allocations.settledAt', {
                    rate: allocation.appliedRate,
                    usd: formatNumber(allocation.usd, 2),
                  })}
                  {difference === 0 ? null : (
                    <span
                      className={cn(
                        'ml-1.5 font-medium',
                        difference < 0 ? 'text-negative' : 'text-positive',
                      )}
                      title={t('finance.allocations.fxDifferenceHint')}
                    >
                      {t('finance.allocations.fxDifference', {
                        value: formatNumber(allocation.fxSettlementDifference, 4),
                      })}
                    </span>
                  )}
                </span>
                <span className="text-text-muted text-[12px] font-light whitespace-nowrap @2xl:text-right">
                  {formatTimestamp(allocation.allocatedAt)}
                </span>
              </li>
            )
          })}
        </ul>
        </>
      )}

      {/* Closed from onSuccess only, like every other write on this page: a
          refusal leaves the sheet open holding the splits rather than closing
          over an allocation that never landed. */}
      <PaymentAllocationSheet
        open={open}
        onClose={() => setOpen(false)}
        payment={payment}
        pending={allocate.isPending}
        submitError={allocate.isError ? allocate.error.message : undefined}
        /* Outbound money settles a COST; inbound money settles a tranche, and
           for that the sheet reads the case for itself exactly as it always
           has. Supplying targets is what turns the picker around. */
        targets={
          outbound
            ? {
                kind: 'expense',
                options: expenseOptions,
                notice: t('finance.allocations.expenseTargetsNote'),
                emptyNotice: t('finance.allocations.expenseTargetsNone'),
              }
            : undefined
        }
        onSubmit={(splits) =>
          allocate.mutate(
            { paymentId: payment.id, payload: toAllocationPayload(splits, currency) },
            { onSuccess: () => setOpen(false) },
          )
        }
      />

      {/* The reason sheet, shared with the direction page's settlements table
          so the wording and the mandatory-reason rule cannot drift between the
          two places the act is offered. */}
      <RejectPaymentSheet
        open={rejecting}
        onClose={() => setRejecting(false)}
        payment={payment}
      />
    </div>
  )
}
